/**
 * The six symbols that are not in the sans face — and must not be.
 *
 * `design-system/README.md` §2.5 makes three redundancies mandatory rather than
 * optional, because UP/DOWN is green/red and here it encodes money: a glyph
 * beside every directional value, an explicit sign on every delta, and a fixed
 * left/right position. This is the first of the three.
 *
 * **Why it is a component and not a character.** `design-system/fonts.md`
 * settled that `▲`/`▼` live in the mono range, so that a directional glyph
 * shares a baseline and an advance width with the number it sits beside — split
 * across two faces it gets neither, and on a 22px orderbook row that is the 1–2px
 * column jitter the fixed-row rule exists to prevent. `gen:fonts` puts all six
 * in `jbmono-glyphs.woff2` and *only* there, so a `▲` typed into a sans string
 * would fall through to whatever the platform has. Rendering them through this
 * component is what keeps that from happening: it sets `--ud-font-mono`
 * explicitly, whatever family its surroundings use.
 *
 * The glyph is `aria-hidden` and the meaning is carried by `label`, because a
 * screen reader announcing "black up-pointing triangle" beside a price is worse
 * than one that says "up".
 */

import { cx } from '@/lib/cx'
import { DOWN_GLYPH, UP_GLYPH } from '@/lib/format'

export const GLYPHS = {
  // The two directional glyphs come from `lib/format`, which already owns them
  // for the strings it builds. Two spellings of `▲` in one codebase is one too
  // many, and `lib/` is where a value that both a formatter and a component need
  // belongs.
  up: UP_GLYPH,
  down: DOWN_GLYPH,
  arrow: '→', // → — the settlement window, `21:46:00 → 21:46:10`
  caret: '▸', // ▸ — the next-markets rail
  check: '✓', // ✓ — the SETTLED pill
} as const

export type GlyphName = keyof typeof GLYPHS

const DEFAULT_LABELS: Record<GlyphName, string | undefined> = {
  up: 'up',
  down: 'down',
  arrow: 'to',
  caret: undefined,
  check: undefined,
}

export interface GlyphProps {
  name: GlyphName
  /** Announced instead of the character. `null` marks it purely decorative. */
  label?: string | null
  className?: string
}

export function Glyph({ name, label, className }: GlyphProps) {
  const announced = label === undefined ? DEFAULT_LABELS[name] : label
  return (
    <>
      <span
        aria-hidden="true"
        data-glyph={name}
        className={cx('font-mono not-italic', className)}
      >
        {GLYPHS[name]}
      </span>
      {announced ? <span className="sr-only">{announced}</span> : null}
    </>
  )
}

/**
 * The glyph for a direction, with `null` for flat.
 *
 * A position can be `FLAT` (§F16 put `FLAT` into `openapi`'s outcome enum), and
 * flat has no direction — rendering `▲` for it would be a lie, and rendering
 * nothing keeps the column aligned because the cell is `.ud-num` and tabular.
 */
export function directionGlyph(direction: 'UP' | 'DOWN' | 'FLAT' | null): GlyphName | null {
  if (direction === 'UP') return 'up'
  if (direction === 'DOWN') return 'down'
  return null
}
